// File Name: lib/ynoMatchDb.js
//
// Read-only access to the YNO **app's** Firestore, for the public match pages
// (/join and /live).
//
// Two things to know before touching this file:
//
// 1. The match data lives in a DIFFERENT Firebase project from this website.
//    The site is `yalla-nellab-12650` (see lib/firebase.js); matches are in the
//    app's project `yno-app-e96f5`. So this initialises a SECOND, *named*
//    Firebase app. It must stay named — calling initializeApp() without a name
//    would clobber the site's default app and break auth everywhere.
//
// 2. Every read here is unauthenticated, and that is intended. The app's
//    firestore.rules has `match /matches/{mid} { allow read: if true }` plus the
//    same on every subcollection — the rule that lets an accountless guest
//    resolve a match code inside the app. The API key below is a public
//    identifier, not a secret: the same key already ships inside the Android
//    APK. Access is governed by the rules, not by hiding the key.
//
// This module NEVER writes. The website is a spectator view.

import { initializeApp, getApps } from 'firebase/app';
// Imported locally AND re-exported below: `export ... from` alone would not
// bring these into this module's scope, and findMatchByCode needs them.
import { toMillis, normaliseCode } from './matchTime';
import {
  getFirestore,
  collection,
  doc,
  query,
  where,
  limit,
  getDocs,
  onSnapshot,
} from 'firebase/firestore';

const YNO_APP_NAME = 'yno-match';

// The app's web config (lib/firebase_options.dart -> FirebaseOptions.web).
const ynoConfig = {
  apiKey: 'AIzaSyAC0lgsD_K86fhmpwaZWpEoFE6rVjMLafU',
  authDomain: 'yno-app-e96f5.firebaseapp.com',
  projectId: 'yno-app-e96f5',
  storageBucket: 'yno-app-e96f5.firebasestorage.app',
  messagingSenderId: '1082478194715',
  appId: '1:1082478194715:web:5f910f904c51234ca2c8be',
};

export const ynoApp =
  getApps().find((a) => a.name === YNO_APP_NAME) ||
  initializeApp(ynoConfig, YNO_APP_NAME);

export const matchDb = getFirestore(ynoApp);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Pure helpers live in matchTime.js so the clock can be tested without
// initialising Firebase. Re-exported here for callers that want one import.
export { toMillis, normaliseCode } from './matchTime';

// ---------------------------------------------------------------------------
// Lookup
// ---------------------------------------------------------------------------

/**
 * Resolve a join code to a match.
 *
 * Mirrors MatchRepository.findByCode in the app EXACTLY: the shared `code`
 * first, then each side's code, first hit wins. Do not "optimise" this into a
 * single OR query — the field order is what decides which match a duplicated
 * code resolves to, and the site must agree with the app.
 *
 * @returns {Promise<object|null>} the match with its `id`, or null.
 */
export async function findMatchByCode(rawCode) {
  const code = normaliseCode(rawCode);
  if (!code) return null;

  for (const field of ['code', 'codeA', 'codeB']) {
    const snap = await getDocs(
      query(collection(matchDb, 'matches'), where(field, '==', code), limit(1))
    );
    if (!snap.empty) {
      const d = snap.docs[0];
      return { id: d.id, ...d.data() };
    }
  }
  return null;
}

/**
 * Which team a code joins.
 *
 * Side B only when `codeB` matches; everything else — the shared code included
 * — is side A. That is what the app does, and guest_join_screen has no side
 * picker, so this is a promise the app actually keeps.
 */
export function sideForCode(match, rawCode) {
  const code = normaliseCode(rawCode);
  return match && match.codeB === code ? 'b' : 'a';
}

// ---------------------------------------------------------------------------
// Live subscriptions
// ---------------------------------------------------------------------------

/** @returns unsubscribe */
export function watchMatch(matchId, onChange, onError) {
  return onSnapshot(
    doc(matchDb, 'matches', matchId),
    (snap) => onChange(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    onError
  );
}

/**
 * Roster, used only for the per-side head count.
 *
 * Deliberately unordered: `joinedAt` is written with a server timestamp, and an
 * orderBy would silently drop any document whose timestamp has not resolved
 * yet. We only ever count these, so order is irrelevant.
 */
export function watchPlayers(matchId, onChange, onError) {
  return onSnapshot(
    collection(matchDb, 'matches', matchId, 'players'),
    (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError
  );
}

/**
 * Goals, sorted client-side for the same reason as above: a goal logged seconds
 * ago may still have a pending server timestamp, and an orderBy('at') query
 * would leave it out of the feed until the write resolves.
 */
export function watchGoals(matchId, onChange, onError) {
  return onSnapshot(
    collection(matchDb, 'matches', matchId, 'goals'),
    (snap) => {
      const goals = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      goals.sort((x, y) => {
        const xm = x.minute ?? 0;
        const ym = y.minute ?? 0;
        if (xm !== ym) return xm - ym;
        return (toMillis(x.at) ?? 0) - (toMillis(y.at) ?? 0);
      });
      onChange(goals);
    },
    onError
  );
}
